import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Spinner } from '@/components/ui/spinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'SUPER_ADMIN' | 'CLIENT';
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // 로딩 중
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Spinner size="4xl" className="text-primary mx-auto mb-4" />
          <p className="text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  // 인증되지 않은 경우
  if (!isAuthenticated) {
    // 관리자 전용 경로는 저장하지 않음 (CLIENT 사용자가 로그인 후 admin 경로로 리다이렉트되는 버그 방지)
    const isAdminPath = location.pathname.includes('/admin/');
    if (!isAdminPath) {
      sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    }
    return <Navigate to="/login" replace />;
  }

  // 승인 대기 중
  if (user?.approvalStatus === 'PENDING') {
    return <Navigate to="/pending-approval" replace />;
  }

  // 승인 거부
  if (user?.approvalStatus === 'REJECTED') {
    return <Navigate to="/approval-rejected" replace />;
  }

  // 권한 체크
  if (requiredRole && user?.userRole !== requiredRole) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

export const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  // 이미 로그인된 경우 redirectAfterLogin이 있으면 해당 경로로, 없으면 대시보드로 리다이렉트
  if (isAuthenticated) {
    const saved = sessionStorage.getItem('redirectAfterLogin');
    // 어드민 경로는 일반 사용자에게 리다이렉트하지 않음
    const isAdminPath = saved && saved.includes('/admin/');
    // /dashboard 하위 경로는 항상 허용
    const isDashboardSubPath = saved && saved.startsWith('/dashboard/');
    const redirectTo = saved && (!isAdminPath || isDashboardSubPath) ? saved : '/dashboard';
    // 리다이렉트 직전에 sessionStorage 제거
    if (saved) {
      sessionStorage.removeItem('redirectAfterLogin');
    }
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};
